import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { usePlan } from '../context/PlanContext';
import OrgLLMSettings from '../components/OrgLLMSettings';
import AIModelPicker from '../components/AIModelPicker';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import {
  Buildings,
  Clock,
  UsersThree,
  CreditCard,
  FloppyDisk,
  CircleNotch,
  CurrencyDollar,
  GlobeSimple,
  MagicWand,
  CheckCircle,
  ArrowRight,
  BookOpen,
  UploadSimple,
  File as FileIcon,
  Trash,
  DownloadSimple,
  Key,
  Note,
  PencilSimple,
  Eye,
  EyeSlash,
  Plus,
  Storefront,
  Copy,
  ArrowSquareOut,
} from '@phosphor-icons/react';

const BASE_TABS = [
  { id: 'profile', label: 'Business Profile', icon: Buildings },
  { id: 'hours', label: 'Working Hours', icon: Clock },
  { id: 'pricing', label: 'Pricing', icon: CurrencyDollar },
  { id: 'knowledge', label: 'Knowledge', icon: BookOpen },
];

const DAYS_MAP = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

const InputField = ({ label, value, onChange, placeholder, type = 'text', rows, testId }) => (
  <div>
    <label className="text-xs text-[#B0B8C4] mb-1.5 block">{label}</label>
    {rows ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        data-testid={testId}
        className="w-full px-4 py-2.5 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg text-sm text-[#F0F4F8] placeholder-[#7D8896] focus:outline-none focus:border-[#00E5C0] transition-colors resize-none"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full px-4 py-2.5 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg text-sm text-[#F0F4F8] placeholder-[#7D8896] focus:outline-none focus:border-[#00E5C0] transition-colors"
      />
    )}
  </div>
);

/* ── Business Profile Tab ── */
const ProfileTab = ({ profile, orgId, onSaved, completion, onTabSwitch }) => {
  const [name, setName] = useState(profile?.business_name || '');
  const [industry, setIndustry] = useState(profile?.industry || profile?.business_type || '');
  const [services, setServices] = useState(profile?.services_offered || '');
  const [websiteUrl, setWebsiteUrl] = useState(profile?.website_url || '');
  const [saving, setSaving] = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [autofillResult, setAutofillResult] = useState(null);

  useEffect(() => {
    setName(profile?.business_name || '');
    setIndustry(profile?.industry || profile?.business_type || '');
    setServices(profile?.services_offered || '');
    setWebsiteUrl(profile?.website_url || '');
  }, [profile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ business_name: name, industry, business_type: industry, services_offered: services, website_url: websiteUrl }),
      }, orgId);
      if (res.ok) {
        toast.success('Profile saved');
        setAutofillResult(null);
        onSaved();
      } else {
        toast.error('Failed to save');
      }
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleAutofill = async () => {
    const url = websiteUrl.trim();
    if (!url) { toast.error('Enter your website URL first.'); return; }
    setAutofilling(true);
    setAutofillResult(null);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/profile/autofill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      }, orgId);
      if (res.ok) {
        const data = await res.json();
        const ex = data.extracted || {};
        setAutofillResult(ex);
        // Pre-fill empty fields only
        if (ex.business_name && !name) setName(ex.business_name);
        if (ex.industry && !industry) setIndustry(ex.industry);
        if (ex.services_offered && !services) setServices(ex.services_offered);
        toast.success('Details extracted. Review and save when ready.');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not extract details from this website.');
      }
    } catch { toast.error('Could not reach the website.'); }
    setAutofilling(false);
  };

  const applyAutofill = (field, value) => {
    if (field === 'name') setName(value);
    if (field === 'industry') setIndustry(value);
    if (field === 'services') setServices(value);
  };

  // Completion status for profile and hours
  const profileDone = completion?.completed_fields?.includes('business_name') && completion?.completed_fields?.includes('services_offered');
  const hoursDone = completion?.completed_fields?.includes('working_hours');

  return (
    <div className="space-y-6">
      {/* Business Setup status */}
      <div className="bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl p-4" data-testid="setup-status-section">
        <p className="text-[11px] font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] pb-2 border-b border-[rgba(255,255,255,0.08)] mb-3">Business setup</p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            {profileDone
              ? <CheckCircle size={14} className="text-[#00E5C0]" weight="fill" />
              : <span className="w-3.5 h-3.5 rounded-full border border-[rgba(255,255,255,0.15)]" />
            }
            <span className={`text-xs ${profileDone ? 'text-[#7D8896]' : 'text-[#F0F4F8]'}`}>Profile</span>
          </div>
          <button
            onClick={() => onTabSwitch('hours')}
            className="flex items-center gap-2 group"
            data-testid="setup-nav-hours"
          >
            {hoursDone
              ? <CheckCircle size={14} className="text-[#00E5C0]" weight="fill" />
              : <span className="w-3.5 h-3.5 rounded-full border border-[rgba(255,255,255,0.15)]" />
            }
            <span className={`text-xs ${hoursDone ? 'text-[#7D8896]' : 'text-[#F0F4F8]'}`}>Working hours</span>
            {!hoursDone && <ArrowRight size={10} className="text-[#7D8896] group-hover:text-[#00E5C0] transition-colors" />}
          </button>
        </div>
      </div>

      {/* Website URL + Autofill */}
      <div>
        <label className="text-xs text-[#B0B8C4] mb-1.5 block">Website URL (optional)</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <GlobeSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7D8896]" />
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="www.yourbusiness.com"
              data-testid="settings-website-url"
              className="w-full pl-9 pr-4 py-2.5 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg text-sm text-[#F0F4F8] placeholder-[#7D8896] focus:outline-none focus:border-[#00E5C0] transition-colors"
            />
          </div>
          <button
            onClick={handleAutofill}
            disabled={autofilling || !websiteUrl.trim()}
            data-testid="autofill-btn"
            className="flex items-center gap-1.5 px-4 py-2.5 bg-[rgba(99,102,241,0.08)] text-[#818CF8] text-xs font-medium rounded-lg border border-[rgba(99,102,241,0.15)] hover:bg-[rgba(99,102,241,0.15)] transition-all disabled:opacity-40 shrink-0"
          >
            {autofilling ? <CircleNotch size={13} className="animate-spin" /> : <MagicWand size={13} />}
            Auto-fill my details
          </button>
        </div>
      </div>

      {/* Autofill suggestions */}
      {autofillResult && (
        <div className="bg-[rgba(99,102,241,0.04)] border border-[rgba(99,102,241,0.1)] rounded-xl p-4 space-y-2" data-testid="autofill-suggestions">
          <p className="text-[11px] font-medium text-[#818CF8] mb-2">Extracted from your website. Review below and save when ready.</p>
          {autofillResult.business_name && autofillResult.business_name !== name && (
            <button onClick={() => applyAutofill('name', autofillResult.business_name)}
              className="flex items-center gap-2 text-[11px] text-[#B0B8C4] hover:text-[#F0F4F8] transition-colors" data-testid="apply-autofill-name">
              <ArrowRight size={10} className="text-[#818CF8]" />
              Use "{autofillResult.business_name}" as business name
            </button>
          )}
          {autofillResult.industry && autofillResult.industry !== industry && (
            <button onClick={() => applyAutofill('industry', autofillResult.industry)}
              className="flex items-center gap-2 text-[11px] text-[#B0B8C4] hover:text-[#F0F4F8] transition-colors" data-testid="apply-autofill-industry">
              <ArrowRight size={10} className="text-[#818CF8]" />
              Use "{autofillResult.industry}" as industry
            </button>
          )}
          {autofillResult.services_offered && autofillResult.services_offered !== services && (
            <button onClick={() => applyAutofill('services', autofillResult.services_offered)}
              className="flex items-center gap-2 text-[11px] text-[#B0B8C4] hover:text-[#F0F4F8] transition-colors" data-testid="apply-autofill-services">
              <ArrowRight size={10} className="text-[#818CF8]" />
              Use extracted services list
            </button>
          )}
        </div>
      )}

      <InputField label="Business Name" value={name} onChange={setName} placeholder="Your business name" testId="settings-business-name" />
      <InputField label="Industry" value={industry} onChange={setIndustry} placeholder="e.g. Plumbing, HVAC, Electrical" testId="settings-industry" />
      <InputField label="Services Offered" value={services} onChange={setServices} placeholder="List your main services" rows={3} testId="settings-services" />
      <button
        onClick={handleSave}
        disabled={saving}
        data-testid="save-profile-btn"
        className="flex items-center gap-2 px-4 py-2.5 bg-[#00E5C0] text-[#12161C] text-sm font-medium rounded-lg hover:bg-[#00CCB0] transition-colors disabled:opacity-50"
      >
        {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
        Save changes
      </button>
    </div>
  );
};

/* ── Working Hours Tab ── */
const HoursTab = ({ profile, orgId, onSaved }) => {
  const defaultHours = DAYS_MAP.reduce((acc, d) => ({
    ...acc,
    [d.key]: { enabled: !['sat', 'sun'].includes(d.key), start: '08:00', end: '17:00' },
  }), {});

  const [hours, setHours] = useState(profile?.working_hours || defaultHours);
  const [buffer, setBuffer] = useState(profile?.buffer_between_jobs || profile?.buffer_minutes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setHours(profile?.working_hours || defaultHours);
    setBuffer(profile?.buffer_between_jobs || profile?.buffer_minutes || '');
  }, [profile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ working_hours: hours, buffer_between_jobs: buffer || undefined }),
      }, orgId);
      if (res.ok) {
        toast.success('Working hours saved');
        onSaved();
      } else {
        toast.error('Failed to save');
      }
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      {DAYS_MAP.map((d) => {
        const day = hours[d.key] || { enabled: false, start: '08:00', end: '17:00' };
        return (
          <div key={d.key} className="flex items-center gap-4 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg px-4 py-3">
            <label className="flex items-center gap-3 w-32">
              <input
                type="checkbox"
                checked={day.enabled}
                onChange={(e) => setHours((h) => ({ ...h, [d.key]: { ...h[d.key], enabled: e.target.checked } }))}
                className="rounded border-[rgba(255,255,255,0.2)] bg-transparent text-[#00E5C0] focus:ring-[#00E5C0]"
              />
              <span className="text-sm text-[#F0F4F8]">{d.label}</span>
            </label>
            {day.enabled ? (
              <div className="flex items-center gap-2">
                <input type="time" value={day.start} onChange={(e) => setHours((h) => ({ ...h, [d.key]: { ...h[d.key], start: e.target.value } }))}
                  className="px-3 py-1.5 bg-[#161C24] border border-[rgba(255,255,255,0.08)] rounded-lg text-xs text-[#F0F4F8] focus:outline-none" />
                <span className="text-xs text-[#7D8896]">to</span>
                <input type="time" value={day.end} onChange={(e) => setHours((h) => ({ ...h, [d.key]: { ...h[d.key], end: e.target.value } }))}
                  className="px-3 py-1.5 bg-[#161C24] border border-[rgba(255,255,255,0.08)] rounded-lg text-xs text-[#F0F4F8] focus:outline-none" />
              </div>
            ) : (
              <span className="text-xs text-[#7D8896]">Closed</span>
            )}
          </div>
        );
      })}
      <div className="pt-2">
        <label className="text-xs text-[#B0B8C4] block mb-1.5">Buffer between jobs (minutes)</label>
        <input
          type="number"
          value={buffer}
          onChange={(e) => setBuffer(e.target.value)}
          placeholder="e.g. 15"
          data-testid="settings-buffer"
          className="w-24 px-3 py-2 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg text-sm text-[#F0F4F8] placeholder-[#7D8896] focus:outline-none focus:border-[#00E5C0]"
        />
      </div>
      <button
        onClick={handleSave}
        disabled={saving}
        data-testid="save-hours-btn"
        className="mt-4 flex items-center gap-2 px-4 py-2.5 bg-[#00E5C0] text-[#12161C] text-sm font-medium rounded-lg hover:bg-[#00CCB0] transition-colors disabled:opacity-50"
      >
        {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
        Save hours
      </button>
    </div>
  );
};

/* ── Pricing Tab ── */
const PricingTab = ({ profile, orgId, onSaved }) => {
  const [notes, setNotes] = useState(profile?.pricing_notes || '');
  const [callout, setCallout] = useState(profile?.callout_fee || profile?.callout_fee_text || '');
  const [hourly, setHourly] = useState(profile?.hourly_rate || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNotes(profile?.pricing_notes || '');
    setCallout(profile?.callout_fee || profile?.callout_fee_text || '');
    setHourly(profile?.hourly_rate || '');
  }, [profile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pricing_notes: notes, callout_fee: callout, hourly_rate: hourly }),
      }, orgId);
      if (res.ok) {
        toast.success('Pricing saved');
        onSaved();
      } else {
        toast.error('Failed to save');
      }
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <InputField label="Pricing Notes" value={notes} onChange={setNotes} placeholder="e.g. Standard jobs start at $150. Hourly rate $95/hr plus materials." rows={3} testId="settings-pricing-notes" />
      <InputField label="Callout Fee" value={callout} onChange={setCallout} placeholder="e.g. $80" testId="settings-callout" />
      <InputField label="Hourly Rate" value={hourly} onChange={setHourly} placeholder="e.g. $95/hr" testId="settings-hourly" />
      <button
        onClick={handleSave}
        disabled={saving}
        data-testid="save-pricing-btn"
        className="flex items-center gap-2 px-4 py-2.5 bg-[#00E5C0] text-[#12161C] text-sm font-medium rounded-lg hover:bg-[#00CCB0] transition-colors disabled:opacity-50"
      >
        {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
        Save pricing
      </button>
    </div>
  );
};

/* ── Team Tab ── */
const TeamTab = ({ navigate }) => (
  <div className="text-center py-10">
    <UsersThree size={40} className="text-[#7D8896] mx-auto mb-3" />
    <p className="text-sm text-[#B0B8C4] mb-4">Manage your team members and access</p>
    <button
      onClick={() => navigate('/settings/team')}
      className="px-4 py-2.5 bg-[#161C24] text-[#F0F4F8] text-sm font-medium rounded-lg border border-[rgba(255,255,255,0.08)] hover:bg-[rgba(255,255,255,0.04)] transition-colors"
      data-testid="manage-team-btn"
    >
      Manage team
    </button>
  </div>
);

/* ── Billing Tab ── */
const BillingTab = ({ navigate, plan }) => (
  <div className="space-y-6">
    <div className="bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl p-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-[#F0F4F8]">Current Plan</h3>
          <p className="text-2xl font-semibold text-[#00E5C0] mt-1">{
            plan === 'partner_pro' || plan === 'partner-pro' ? 'Partner Pro' :
            plan === 'teams' || plan === 'pro' ? 'Teams' :
            plan === 'professional' || plan === 'builder' ? 'Professional' : 'Basic'
          }</p>
        </div>
        <button
          onClick={() => navigate('/billing')}
          className="px-4 py-2 text-xs text-[#F0F4F8] border border-[rgba(255,255,255,0.08)] rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-colors"
          data-testid="manage-billing-btn"
        >
          Manage billing
        </button>
      </div>
    </div>
  </div>
);

/* ── Knowledge Tab ── */
const SNIPPET_MAX_CHARS = 5000;

const SnippetsSection = ({ orgId }) => {
  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [label, setLabel] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchSnippets = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/knowledge/snippets`, {}, orgId);
      if (res.ok) {
        const data = await res.json();
        setSnippets(data.snippets || []);
      }
    } catch {}
    setLoading(false);
  }, [orgId]);

  useEffect(() => { fetchSnippets(); }, [fetchSnippets]);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setLabel('');
    setContent('');
  };

  const openCreate = () => {
    setEditingId(null);
    setLabel('');
    setContent('');
    setShowForm(true);
  };

  const openEdit = (snip) => {
    setEditingId(snip.id);
    setLabel(snip.label || '');
    setContent(snip.content || '');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!label.trim() || !content.trim()) {
      toast.error('Both label and content are required.');
      return;
    }
    if (content.length > SNIPPET_MAX_CHARS) {
      toast.error(`Content too long. Max ${SNIPPET_MAX_CHARS} characters.`);
      return;
    }
    setSaving(true);
    try {
      const url = editingId
        ? `${BACKEND_URL}/api/service-os/knowledge/snippet/${editingId}`
        : `${BACKEND_URL}/api/service-os/knowledge/snippet`;
      const method = editingId ? 'PATCH' : 'POST';
      const res = await fetchWithOrg(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: label.trim(), content: content.trim() }),
      }, orgId);
      if (res.ok) {
        toast.success(editingId ? 'Snippet updated.' : 'Snippet added.');
        resetForm();
        fetchSnippets();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save snippet.');
      }
    } catch { toast.error('Could not save snippet.'); }
    setSaving(false);
  };

  const handleToggle = async (snip) => {
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/knowledge/snippet/${snip.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled: !snip.enabled }),
        },
        orgId
      );
      if (res.ok) fetchSnippets();
    } catch { toast.error('Could not update snippet.'); }
  };

  const handleDelete = async (snip) => {
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/knowledge/snippet/${snip.id}`,
        { method: 'DELETE' },
        orgId
      );
      if (res.ok) {
        toast.success(`"${snip.label}" removed.`);
        fetchSnippets();
      }
    } catch { toast.error('Could not delete snippet.'); }
  };

  return (
    <div data-testid="knowledge-snippets-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-[#F0F4F8]">Knowledge snippets</h3>
          <p className="text-[11px] text-[#7D8896] mt-0.5">
            Pasted text (SOPs, brand voice, FAQs) auto-injected into reply prompts.
          </p>
        </div>
        {!showForm && (
          <button
            onClick={openCreate}
            data-testid="add-snippet-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[rgba(0,229,192,0.06)] text-[#00E5C0] text-xs font-medium rounded-lg border border-[rgba(0,229,192,0.12)] hover:bg-[rgba(0,229,192,0.12)] transition-all"
          >
            <Plus size={12} weight="bold" />
            Add snippet
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-[#1F2937] border border-[rgba(0,229,192,0.18)] rounded-xl p-4 mb-3" data-testid="snippet-form">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value.slice(0, 120))}
            placeholder="Label (e.g. Brand voice, Service rates 2026)"
            data-testid="snippet-label-input"
            className="w-full bg-[#111827] border border-[rgba(255,255,255,0.08)] rounded-lg px-3 py-2 text-xs text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[#00E5C0] mb-2"
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value.slice(0, SNIPPET_MAX_CHARS))}
            placeholder="Paste the content workers should reference. Plain text only."
            rows={6}
            data-testid="snippet-content-input"
            className="w-full bg-[#111827] border border-[rgba(255,255,255,0.08)] rounded-lg px-3 py-2 text-xs text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[#00E5C0] resize-none"
          />
          <div className="flex items-center justify-between mt-2">
            <span className={`text-[10px] ${content.length > SNIPPET_MAX_CHARS * 0.9 ? 'text-[#F59E0B]' : 'text-[#5C6778]'}`}>
              {content.length} / {SNIPPET_MAX_CHARS}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={resetForm}
                disabled={saving}
                data-testid="snippet-cancel-btn"
                className="px-3 py-1.5 text-[11px] text-[#B0B8C4] hover:text-[#F0F4F8] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !label.trim() || !content.trim()}
                data-testid="snippet-save-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00E5C0] text-[#0B0F14] text-[11px] font-semibold rounded-lg hover:bg-[#00D0B0] transition-colors disabled:opacity-50"
              >
                {saving ? <CircleNotch size={12} className="animate-spin" /> : <FloppyDisk size={12} weight="bold" />}
                {editingId ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <CircleNotch size={18} className="text-[#6366F1] animate-spin" />
        </div>
      ) : snippets.length === 0 && !showForm ? (
        <div className="text-center py-6 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl">
          <Note size={28} className="text-[#7D8896] mx-auto mb-2" />
          <p className="text-[11px] text-[#7D8896]">No snippets yet. Add brand voice notes, FAQs, or service rates.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="snippet-list">
          {snippets.map((snip) => (
            <div
              key={snip.id}
              data-testid={`snippet-${snip.id}`}
              className={`bg-[#1F2937] border rounded-xl px-4 py-3 ${snip.enabled ? 'border-[rgba(255,255,255,0.08)]' : 'border-[rgba(255,255,255,0.04)] opacity-60'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-8 h-8 rounded-lg bg-[rgba(0,229,192,0.08)] flex items-center justify-center shrink-0">
                    <Note size={14} className="text-[#00E5C0]" weight="duotone" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-[#F0F4F8] truncate">{snip.label}</p>
                    <p className="text-[10px] text-[#7D8896] line-clamp-2 mt-0.5">{snip.content}</p>
                    <p className="text-[10px] text-[#5C6778] mt-1">
                      {snip.char_count} chars {snip.enabled ? '· active' : '· paused'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleToggle(snip)}
                    data-testid={`snippet-toggle-${snip.id}`}
                    title={snip.enabled ? 'Pause injection' : 'Resume injection'}
                    className="p-2 rounded-lg text-[#7D8896] hover:text-[#F0F4F8] hover:bg-[rgba(255,255,255,0.04)] transition-colors"
                  >
                    {snip.enabled ? <Eye size={14} /> : <EyeSlash size={14} />}
                  </button>
                  <button
                    onClick={() => openEdit(snip)}
                    data-testid={`snippet-edit-${snip.id}`}
                    className="p-2 rounded-lg text-[#7D8896] hover:text-[#F0F4F8] hover:bg-[rgba(255,255,255,0.04)] transition-colors"
                  >
                    <PencilSimple size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(snip)}
                    data-testid={`snippet-delete-${snip.id}`}
                    className="p-2 rounded-lg text-[#7D8896] hover:text-[#EF4444] hover:bg-[rgba(239,68,68,0.06)] transition-colors"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const KnowledgeTab = ({ orgId }) => {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = React.useRef(null);

  const fetchDocs = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/knowledge`, {}, orgId);
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch {}
    setLoading(false);
  }, [orgId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/knowledge`, {
        method: 'POST',
        body: formData,
      }, orgId);
      if (res.ok) {
        toast.success(`"${file.name}" uploaded.`);
        fetchDocs();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed.');
      }
    } catch { toast.error('Upload failed.'); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (docId, filename) => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/knowledge/${docId}`, {
        method: 'DELETE',
      }, orgId);
      if (res.ok) {
        toast.success(`"${filename}" removed.`);
        fetchDocs();
      } else {
        toast.error('Could not remove document.');
      }
    } catch { toast.error('Could not remove document.'); }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/knowledge/${docId}/download`, {}, orgId);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch { toast.error('Download failed.'); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6" data-testid="knowledge-tab">
      <SnippetsSection orgId={orgId} />

      <div className="border-t border-[rgba(255,255,255,0.06)] pt-6">
        <h3 className="text-sm font-semibold text-[#F0F4F8] mb-1">Reference documents</h3>
        <p className="text-xs text-[#B0B8C4] mb-4">
          Upload documents your workers can reference when handling enquiries, drafting quotes, or responding to customers.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt,.csv,.md,.rtf"
          onChange={handleUpload}
          className="hidden"
          data-testid="knowledge-file-input"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="knowledge-upload-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-[rgba(0,229,192,0.06)] text-[#00E5C0] text-sm font-medium rounded-lg border border-[rgba(0,229,192,0.12)] hover:bg-[rgba(0,229,192,0.12)] transition-all disabled:opacity-50"
        >
          {uploading ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />}
          {uploading ? 'Uploading...' : 'Upload document'}
        </button>
        <p className="text-[10px] text-[#5C6778] mt-2">Supported: PDF, DOC, DOCX, TXT, CSV, MD. Max 10 MB.</p>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <CircleNotch size={20} className="text-[#6366F1] animate-spin" />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-8 mt-4 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl">
            <BookOpen size={32} className="text-[#7D8896] mx-auto mb-2" />
            <p className="text-xs text-[#7D8896]">No documents yet. Upload pricing sheets, service guides, or FAQs.</p>
          </div>
        ) : (
          <div className="space-y-2 mt-4" data-testid="knowledge-list">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl px-4 py-3" data-testid={`knowledge-doc-${doc.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-[rgba(99,102,241,0.08)] flex items-center justify-center shrink-0">
                    <FileIcon size={14} className="text-[#818CF8]" weight="duotone" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#F0F4F8] truncate">{doc.original_filename}</p>
                    <p className="text-[10px] text-[#5C6778]">{formatSize(doc.size)} &middot; {doc.ext.toUpperCase()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleDownload(doc.id, doc.original_filename)}
                    className="p-2 rounded-lg text-[#7D8896] hover:text-[#F0F4F8] hover:bg-[rgba(255,255,255,0.04)] transition-colors"
                    data-testid={`download-${doc.id}`}>
                    <DownloadSimple size={14} />
                  </button>
                  <button onClick={() => handleDelete(doc.id, doc.original_filename)}
                    className="p-2 rounded-lg text-[#7D8896] hover:text-[#EF4444] hover:bg-[rgba(239,68,68,0.06)] transition-colors"
                    data-testid={`delete-${doc.id}`}>
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Customer Portal Tab (Teams + Partner Pro only) ── */
const PortalTab = ({ orgId }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slugInput, setSlugInput] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/portal-settings`, {}, orgId);
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        if (data.slug) setSlugInput(data.slug);
      }
    } catch {}
    setLoading(false);
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const updateSettings = async (patch) => {
    setSaving(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/portal-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      }, orgId);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSettings(data);
        if (data.slug) setSlugInput(data.slug);
        toast.success('Portal updated.');
      } else {
        toast.error(data.detail || 'Could not update portal.');
      }
    } catch { toast.error('Could not update portal.'); }
    setSaving(false);
  };

  const handleToggle = () => {
    if (!settings) return;
    if (!settings.slug && !slugInput.trim()) {
      toast.error('Set a portal URL first.');
      return;
    }
    updateSettings({
      enabled: !settings.enabled,
      ...(settings.slug ? {} : { slug: slugInput.trim().toLowerCase() }),
    });
  };

  const handleSaveSlug = () => {
    const cleaned = slugInput.trim().toLowerCase();
    if (cleaned.length < 2) {
      toast.error('Portal URL must be at least 2 characters.');
      return;
    }
    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(cleaned)) {
      toast.error('Use lowercase letters, numbers, hyphens. Cannot start/end with a hyphen.');
      return;
    }
    updateSettings({ slug: cleaned });
  };

  const handleCopy = () => {
    if (!settings?.portal_url) return;
    navigator.clipboard.writeText(settings.portal_url);
    toast.success('Portal URL copied.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <CircleNotch size={20} className="text-[#6366F1] animate-spin" />
      </div>
    );
  }

  if (!settings?.plan_eligible) {
    return (
      <div className="text-center py-10" data-testid="portal-not-eligible">
        <Storefront size={32} className="text-[#7D8896] mx-auto mb-3" />
        <h3 className="text-sm font-semibold text-[#F0F4F8] mb-1">Customer Portal</h3>
        <p className="text-xs text-[#B0B8C4] max-w-sm mx-auto">
          Available on Teams and Partner Pro plans. Upgrade to give your customers a branded
          self-serve portal where they can view jobs and message you directly.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-tab">
      <div>
        <h3 className="text-sm font-semibold text-[#F0F4F8] mb-1">Customer Portal</h3>
        <p className="text-xs text-[#B0B8C4]">
          A branded, secure portal where your customers sign in with magic-link to view their jobs and message you directly.
        </p>
      </div>

      {/* Slug */}
      <div className="bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
        <label className="block text-xs font-medium text-[#F0F4F8] mb-1">Portal URL</label>
        <p className="text-[10px] text-[#7D8896] mb-2">Lowercase letters, numbers, and hyphens. Example: <code>jacks-plumbing</code></p>
        <div className="flex items-stretch gap-2">
          <div className="flex-1 flex items-center bg-[#111827] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden focus-within:border-[#00E5C0]">
            <span className="text-[10px] text-[#5C6778] px-2 select-none">/portal/</span>
            <input
              type="text"
              value={slugInput}
              onChange={(e) => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="your-business"
              data-testid="portal-slug-input"
              className="flex-1 py-2 pr-3 bg-transparent text-xs text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none"
            />
          </div>
          <button
            onClick={handleSaveSlug}
            disabled={saving || !slugInput.trim() || slugInput.trim() === settings?.slug}
            data-testid="portal-save-slug-btn"
            className="px-4 py-2 bg-[rgba(0,229,192,0.06)] text-[#00E5C0] text-xs font-medium rounded-lg border border-[rgba(0,229,192,0.12)] hover:bg-[rgba(0,229,192,0.12)] transition-colors disabled:opacity-50"
          >
            {saving ? <CircleNotch size={12} className="animate-spin" /> : 'Save'}
          </button>
        </div>
      </div>

      {/* Enable toggle */}
      <div className="bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-medium text-[#F0F4F8]">Portal status</p>
            <p className="text-[10px] text-[#7D8896] mt-0.5">
              {settings?.enabled ? 'Live — customers can sign in.' : 'Disabled — portal is hidden.'}
            </p>
          </div>
          <button
            onClick={handleToggle}
            disabled={saving || !settings?.slug}
            data-testid="portal-toggle-btn"
            className={`relative inline-flex h-6 w-11 rounded-full transition-colors disabled:opacity-50 ${
              settings?.enabled ? 'bg-[#00E5C0]' : 'bg-[rgba(255,255,255,0.12)]'
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 bg-white rounded-full transition-transform ${
                settings?.enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Share URL */}
      {settings?.enabled && settings?.portal_url && (
        <div className="bg-[#1F2937] border border-[rgba(0,229,192,0.18)] rounded-xl p-4" data-testid="portal-share-card">
          <p className="text-xs font-medium text-[#F0F4F8] mb-2">Share this link with customers</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[10px] text-[#00E5C0] bg-[#111827] border border-[rgba(0,229,192,0.12)] rounded px-3 py-2 truncate" data-testid="portal-share-url">
              {settings.portal_url}
            </code>
            <button
              onClick={handleCopy}
              data-testid="portal-copy-btn"
              className="px-3 py-2 bg-[rgba(0,229,192,0.06)] text-[#00E5C0] text-xs font-medium rounded-lg border border-[rgba(0,229,192,0.12)] hover:bg-[rgba(0,229,192,0.12)] transition-colors"
              title="Copy"
            >
              <Copy size={14} />
            </button>
            <a
              href={settings.portal_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="portal-open-btn"
              className="px-3 py-2 bg-[rgba(0,229,192,0.06)] text-[#00E5C0] text-xs font-medium rounded-lg border border-[rgba(0,229,192,0.12)] hover:bg-[rgba(0,229,192,0.12)] transition-colors"
              title="Open"
            >
              <ArrowSquareOut size={14} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Main Settings Page ── */
export default function ServiceSettings() {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { plan } = usePlan();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'profile';

  const isPartnerPro = plan === 'partner_pro';

  // Build tabs dynamically — Team tab for Teams/Partner Pro, LLM Key for Partner Pro
  const isTeamPlan = plan === 'teams' || plan === 'pro' || plan === 'partner_pro';
  // Customer Portal: Teams + Partner Pro only (excludes "pro" Professional)
  const isPortalEligible = plan === 'teams' || plan === 'team' || plan === 'partner_pro';

  const tabs = [
    ...BASE_TABS,
    ...(isTeamPlan ? [{ id: 'team', label: 'Team', icon: UsersThree }] : []),
    ...(isPortalEligible ? [{ id: 'portal', label: 'Customer Portal', icon: Storefront }] : []),
    { id: 'ai_model', label: 'AI Model', icon: MagicWand },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    ...(isPartnerPro ? [{ id: 'llm', label: 'LLM Key', icon: Key }] : []),
  ];

  const [activeTab, setActiveTab] = useState(BASE_TABS.some(t => t.id === initialTab) ? initialTab : 'profile');

  // Handle Team/LLM/Portal tab selection after plan loads
  useEffect(() => {
    if (isPartnerPro && initialTab === 'llm') {
      setActiveTab('llm');
    }
    if (isTeamPlan && initialTab === 'team') {
      setActiveTab('team');
    }
    if (isPortalEligible && initialTab === 'portal') {
      setActiveTab('portal');
    }
  }, [isPartnerPro, isTeamPlan, isPortalEligible, initialTab]);
  const [profile, setProfile] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [loading, setLoading] = useState(true);

  const orgId = activeOrganization?.organization_id;

  const fetchProfile = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/profile`, {}, orgId);
      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile);
        setCompletion(data.completion);
      }
    } catch {}
    setLoading(false);
  }, [orgId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  return (
    <>
      <SEO title="Settings" />
      <div className="max-w-[800px] mx-auto" data-testid="service-settings">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#F0F4F8]">Settings</h1>
            <p className="text-sm text-[#B0B8C4] mt-1">Manage your business profile and preferences.</p>
          </div>
          {completion && completion.status !== 'completed' && (
            <div className="flex items-center gap-2">
              <div className="w-16 h-1.5 bg-[rgba(255,255,255,0.06)] rounded-full overflow-hidden">
                <div className="h-full bg-[#6366F1] rounded-full" style={{ width: `${completion.percentage}%` }} />
              </div>
              <span className="text-[10px] text-[#818CF8] font-medium">{completion.percentage}% complete</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-[rgba(255,255,255,0.08)] pb-px overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`settings-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                  active
                    ? 'text-[#00E5C0] border-b-2 border-[#00E5C0] -mb-px'
                    : 'text-[#B0B8C4] hover:text-[#F0F4F8]'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-6">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <CircleNotch size={24} className="text-[#6366F1] animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === 'profile' && <ProfileTab profile={profile} orgId={orgId} onSaved={fetchProfile} completion={completion} onTabSwitch={setActiveTab} />}
              {activeTab === 'hours' && <HoursTab profile={profile} orgId={orgId} onSaved={fetchProfile} />}
              {activeTab === 'pricing' && <PricingTab profile={profile} orgId={orgId} onSaved={fetchProfile} />}
              {activeTab === 'knowledge' && <KnowledgeTab orgId={orgId} />}
              {activeTab === 'team' && <TeamTab navigate={navigate} />}
              {activeTab === 'portal' && isPortalEligible && <PortalTab orgId={orgId} />}
              {activeTab === 'ai_model' && <AIModelPicker />}
              {activeTab === 'billing' && <BillingTab navigate={navigate} plan={plan} />}
              {activeTab === 'llm' && isPartnerPro && <OrgLLMSettings />}
            </>
          )}
        </div>
      </div>
    </>
  );
}
